import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, ExternalLink, LifeBuoy } from 'lucide-react';
import { parseAsBoolean, useQueryState } from 'nuqs';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { FEATURE_KEYS, FEATURE_LABEL, PLAN_LIMIT_KEYS, PLAN_LIMIT_LABEL, platformTicketInput, platformTicketMessageInput, type PlatformPlan, type TenantSubscriptionView, type TicketDetail, type TicketRow } from '@barbeiropro/contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { ErrorState, LoadingState } from '@/components/shared/states';
import { Pill } from '@/features/finance/pill';
import { Card, dd, dt, INTERVAL_LABEL, INVOICE_LABEL, SubPill, UsageBars } from '@/features/platform/shared';
import { SUBSCRIPTION_ACTION_LABEL } from '@/features/platform/org-detail-page';
import { TICKET_PRIORITY_LABEL, TICKET_STATUS_LABEL } from '@/features/platform/support-page';
import { api, errorMessage } from '@/lib/api';
import { money } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Section } from './section';

export const subscriptionKey = ['subscription'] as const;

function SupportModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const list = useQuery({ queryKey: ['support', 'tickets'], queryFn: () => api.get<{ data: TicketRow[] } | TicketRow[]>('/support/tickets') });
  const rows = Array.isArray(list.data) ? list.data : (list.data?.data ?? []);
  const detail = useQuery({ queryKey: ['support', 'ticket', openId], enabled: !!openId, queryFn: () => api.get<TicketDetail>(`/support/tickets/${openId}`) });
  const newForm = useForm<z.input<typeof platformTicketInput>>({ resolver: zodResolver(platformTicketInput), defaultValues: { priority: 'normal' } });
  const msgForm = useForm<z.input<typeof platformTicketMessageInput>>({ resolver: zodResolver(platformTicketMessageInput) });
  const create = useMutation({
    mutationFn: (v: unknown) => api.post<{ id: string }>('/support/tickets', v),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['support'] });
      newForm.reset();
      setCreating(false);
      setOpenId(r.id);
      toast.success('Chamado aberto');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const send = useMutation({
    mutationFn: (v: unknown) => api.post(`/support/tickets/${openId}/messages`, v),
    onSuccess: () => {
      msgForm.reset({ body: '' });
      qc.invalidateQueries({ queryKey: ['support'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const t = detail.data;
  return (
    <FormModal
      open
      onOpenChange={(o) => !o && onClose()}
      size="lg"
      title="Falar com o suporte"
      description="Abra um chamado e acompanhe as respostas por aqui."
      footer={
        openId && t ? (
          <form id="msg-form" className="flex w-full flex-col gap-2 sm:flex-row" noValidate onSubmit={msgForm.handleSubmit((v) => send.mutate(v))}>
            <Textarea aria-label="Mensagem" rows={2} className="flex-1" placeholder="Escreva uma mensagem" {...msgForm.register('body')} />
            <Button type="submit" disabled={send.isPending}>Enviar</Button>
          </form>
        ) : creating ? (
          <>
            <Button variant="outline" onClick={() => setCreating(false)}>Voltar</Button>
            <Button type="submit" form="new-ticket" disabled={create.isPending}>Abrir chamado</Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={onClose}>Fechar</Button>
            <Button onClick={() => setCreating(true)}>Novo chamado</Button>
          </>
        )
      }
    >
      {creating ? (
        <form id="new-ticket" noValidate className="grid gap-4" onSubmit={newForm.handleSubmit((v) => create.mutate(v))}>
          <Field id="t-subject" label="Assunto" error={newForm.formState.errors.subject?.message}><Input id="t-subject" {...newForm.register('subject')} /></Field>
          <Field id="t-body" label="Descreva o que precisa" error={newForm.formState.errors.body?.message}><Textarea id="t-body" rows={5} {...newForm.register('body')} /></Field>
        </form>
      ) : openId ? (
        !t ? <LoadingState /> : (
          <div className="grid gap-3">
            <button type="button" className="justify-self-start text-sm underline" onClick={() => setOpenId(null)}>Voltar para a lista</button>
            <h3 className="font-semibold">{t.subject}</h3>
            <ol className="grid gap-3">
              {t.messages.map((m, i) => (
                <li key={i} className={cn('max-w-[90%] rounded-lg border p-3 text-sm', m.side === 'platform' ? 'bg-accent' : 'ml-auto bg-card')}>
                  <p className="mb-1 text-xs text-muted-foreground">{m.side === 'platform' ? 'Suporte' : m.author} · {dt(m.at)}</p>
                  <p className="whitespace-pre-wrap break-words">{m.body}</p>
                </li>
              ))}
            </ol>
          </div>
        )
      ) : list.isLoading ? <LoadingState /> : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nenhum chamado aberto até agora.</p>
      ) : (
        <ul className="divide-y">
          {rows.map((r) => (
            <li key={r.id}>
              <button type="button" onClick={() => setOpenId(r.id)} className="flex w-full flex-wrap items-center justify-between gap-2 py-3 text-left text-sm hover:bg-muted/50">
                <span className="min-w-0 font-medium">{r.subject}</span>
                <span className="flex gap-2"><Pill>{TICKET_STATUS_LABEL[r.status]}</Pill><Pill>{TICKET_PRIORITY_LABEL[r.priority]}</Pill></span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </FormModal>
  );
}

function PlanCard({ plan, current, currentPrice, onPick, disabled }: { plan: PlatformPlan; current: boolean; currentPrice: number; onPick: () => Promise<unknown>; disabled: boolean }) {
  const up = plan.priceCents > currentPrice;
  return (
    <div className={cn('flex flex-col rounded-lg border bg-card p-4', current && 'ring-2 ring-primary/40')}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-semibold">{plan.name}</h3>
        {current && <Pill tone="good">Plano atual</Pill>}
      </div>
      <p className="font-display text-2xl font-bold tabular">{money(plan.priceCents)}<span className="text-sm font-normal text-muted-foreground">/{INTERVAL_LABEL[plan.interval]}</span></p>
      {plan.description && <p className="mt-1 text-sm text-muted-foreground">{plan.description}</p>}
      <ul className="mt-3 grid gap-0.5 text-sm">
        {PLAN_LIMIT_KEYS.map((k) => (
          <li key={k} className="flex justify-between gap-2"><span className="text-muted-foreground">{PLAN_LIMIT_LABEL[k]}</span><span className="tabular">{plan.limits[k] == null ? 'Ilimitado' : plan.limits[k]?.toLocaleString('pt-BR')}</span></li>
        ))}
      </ul>
      <div className="mt-auto pt-4">
        {!current && (
          <ConfirmDialog
            destructive={false}
            trigger={<Button variant={up ? 'default' : 'outline'} className="w-full" disabled={disabled}>{up ? 'Fazer upgrade' : 'Mudar para este plano'}</Button>}
            title={`${up ? 'Upgrade' : 'Mudança'} para ${plan.name}?`}
            description={up ? `O novo plano vale na hora, com limites maiores. Valor: ${money(plan.priceCents)}/${INTERVAL_LABEL[plan.interval]}.` : 'Se o uso atual passar dos limites do novo plano, a mudança é bloqueada e você verá o que precisa ajustar.'}
            confirmLabel="Confirmar mudança"
            onConfirm={onPick}
          />
        )}
      </div>
    </div>
  );
}

export function SubscriptionPage() {
  const qc = useQueryClient();
  const [support, setSupport] = useQueryState('suporte', parseAsBoolean.withDefault(false));
  const q = useQuery({ queryKey: subscriptionKey, queryFn: () => api.get<TenantSubscriptionView>('/subscription') });
  const [busy, setBusy] = useState(false);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: subscriptionKey });
    qc.invalidateQueries({ queryKey: ['subscription-banner'] });
  };
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
      refresh();
    } catch (e) {
      toast.error(errorMessage(e));
      throw e;
    }
  };
  if (q.isLoading) return <LoadingState />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const d = q.data;
  const sub = d.subscription;
  const suspended = d.organizationStatus !== 'active';

  return (
    <div className="grid gap-8">
      <Section title="Assinatura e plano" description="Seu plano, o consumo dos limites e o histórico de cobrança.">
        <div className="grid gap-4 xl:grid-cols-2">
          <Card title={d.plan?.name ?? 'Sem plano'} actions={sub && <SubPill status={sub.status} />}>
            {sub ? (
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
                <dt className="text-muted-foreground">Valor</dt><dd>{money(sub.priceCents)}/{INTERVAL_LABEL[sub.interval]}</dd>
                {sub.status === 'trialing' && (<><dt className="text-muted-foreground">Teste até</dt><dd>{dd(sub.trialEndsAt)}</dd></>)}
                <dt className="text-muted-foreground">Período atual</dt><dd>{dd(sub.currentPeriodStart)} a {dd(sub.currentPeriodEnd)}</dd>
                {sub.graceUntil && (<><dt className="text-muted-foreground">Carência até</dt><dd>{dd(sub.graceUntil)}</dd></>)}
                {sub.cancelAtPeriodEnd && (<><dt className="text-muted-foreground">Cancelamento</dt><dd className="text-destructive">Agendado para {dd(sub.currentPeriodEnd)}</dd></>)}
              </dl>
            ) : (
              <p className="text-sm text-muted-foreground">Sua conta ainda não tem assinatura registrada. Todos os recursos estão liberados.</p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                variant="outline"
                disabled={busy || !d.portalAvailable}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const r = await api.post<{ url: string }>('/subscription/portal');
                    window.open(r.url, '_blank', 'noopener,noreferrer');
                  } catch (e) {
                    toast.error(errorMessage(e));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                <ExternalLink className="size-4" /> Pagamentos e faturas
              </Button>
              {sub && sub.status !== 'canceled' && (sub.cancelAtPeriodEnd ? (
                <ConfirmDialog destructive={false} trigger={<Button variant="outline">Desfazer cancelamento</Button>} title="Manter a assinatura?" description="O cancelamento agendado será desfeito e a cobrança continua normalmente." confirmLabel="Manter assinatura" onConfirm={() => run(() => api.post('/subscription/resume'), 'Cancelamento desfeito')} />
              ) : (
                <ConfirmDialog trigger={<Button variant="outline">Cancelar assinatura</Button>} title="Cancelar a assinatura?" description="O acesso continua até o fim do período já pago. Seus dados são mantidos e você pode exportá-los a qualquer momento." confirmLabel="Cancelar assinatura" onConfirm={() => run(() => api.post('/subscription/cancel'), 'Cancelamento agendado')} />
              ))}
              <Button variant="outline" onClick={() => api.download('/org/export', undefined, 'meus-dados.zip').catch((e) => toast.error(errorMessage(e)))}>
                <Download className="size-4" /> Exportar meus dados
              </Button>
              <Button variant="outline" onClick={() => void setSupport(true)}>
                <LifeBuoy className="size-4" /> Falar com o suporte
              </Button>
            </div>
            {!d.portalAvailable && <p className="mt-2 text-xs text-muted-foreground">O portal de pagamento ainda não está disponível para esta conta (cobrança não configurada).</p>}
            {suspended && <p className="mt-2 text-sm text-destructive">Conta {d.organizationStatus === 'suspended' ? 'suspensa' : 'com exclusão agendada'}{d.suspendedReason ? `: ${d.suspendedReason}` : ''}. Leitura e exportação continuam liberadas.</p>}
          </Card>
          <Card title="Consumo dos limites" description="Atualizado a cada acesso.">
            <UsageBars usage={d.usage} />
          </Card>
        </div>
      </Section>

      <Section title="Planos disponíveis" description="Upgrade vale na hora. Mudar para um plano menor só é possível se o seu uso couber nos limites dele.">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {d.availablePlans.map((p) => (
            <PlanCard key={p.id} plan={p} current={p.id === sub?.planId} currentPrice={sub?.priceCents ?? 0} disabled={suspended} onPick={() => run(() => api.post('/subscription/change-plan', { planId: p.id }), 'Plano atualizado')} />
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Recursos incluídos no seu plano: {FEATURE_KEYS.filter((k) => d.features[k] !== false).map((k) => FEATURE_LABEL[k]).join(', ') || 'nenhum'}.</p>
      </Section>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card title="Faturas">
          {d.invoices.length === 0 ? <p className="text-sm text-muted-foreground">Nenhuma fatura ainda.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1 font-medium">Vencimento</th><th className="py-1 font-medium">Situação</th><th className="py-1 text-right font-medium">Valor</th><th className="py-1" /></tr></thead>
                <tbody>
                  {d.invoices.map((i) => (
                    <tr key={i.id} className="border-t">
                      <td className="py-1.5">{dd(i.dueDate)}</td>
                      <td className="py-1.5"><Pill tone={i.status === 'paid' ? 'good' : i.status === 'overdue' ? 'bad' : 'neutral'}>{INVOICE_LABEL[i.status]}</Pill></td>
                      <td className="tabular py-1.5 text-right">{money(i.amountCents)}</td>
                      <td className="py-1.5 text-right">{i.invoiceUrl && <a className="underline" href={i.invoiceUrl} target="_blank" rel="noreferrer">Abrir</a>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        <Card title="Histórico do plano">
          {d.history.length === 0 ? <p className="text-sm text-muted-foreground">Sem alterações.</p> : (
            <ul className="divide-y text-sm">
              {d.history.map((h) => (
                <li key={h.id} className="py-2">
                  <div className="flex flex-wrap justify-between gap-x-3"><span className="font-medium">{SUBSCRIPTION_ACTION_LABEL[h.action] ?? h.action}</span><span className="tabular text-muted-foreground">{dt(h.at)}</span></div>
                  <p className="text-muted-foreground">{h.fromPlanName ?? '—'} → {h.toPlanName ?? '—'}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
      {support && <SupportModal onClose={() => void setSupport(false)} />}
    </div>
  );
}
